// Core
import styled from 'styled-components';

// Component
import { PLCCardType } from './PLCCard.types';

// Types
import { Theme } from '../../types/theme';

/**
 * Returns the background color based on the type
 * @param type The type of the PLC card
 * @param theme The current theme
 * @returns The background color
 */
const getBackgroundColor = (type: PLCCardType, theme: Theme): string => {
  const typeColorMap = {
    [PLCCardType.analog]: theme.color.plcCard.analogBackgroundColor,
    [PLCCardType.input]: theme.color.plcCard.inputBackgroundColor,
    [PLCCardType.output]: theme.color.plcCard.outputBackgroundColor,
  };

  return typeColorMap[type] || theme.color.plcCard.inputBackgroundColor;
};

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const Container = styled.div<{ type: PLCCardType }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px 8px 0 0;
  background-color: ${({ type, theme }) => getBackgroundColor(type, theme)};
  width: 100%;
  height: 46px;
  padding: 0 16px;
  gap: ${({ type }) => (
    type === PLCCardType.input || type === PLCCardType.analog
      ? '10px'
      : '18px'
  )};
`;

export const IconsContainer = styled.div<{ type: PLCCardType }>`
  position: relative;
  display: flex;
  align-items: center;
  color: white;
  font-size: 20px;
  margin-right: 8px;

  > svg:first-child {
    position: relative;
    left: ${({ type }) => (type === PLCCardType.output ? '14px' : '0')};
    z-index: 2;
  };

  > svg:last-child {
    position: absolute;
    left: 6px;
    z-index: 1;
  };
`;

export const TextContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-right: 1px solid ${({ theme }) => theme.color.plcCard.containerBorderColor};
  border-left: 1px solid ${({ theme }) => theme.color.plcCard.containerBorderColor};
  border-bottom: 1px solid ${({ theme }) => theme.color.plcCard.containerBorderColor};
  border-radius: 0 0 8px 8px;
  background: ${({ theme }) => theme.color.plcCard.containerBackgroundColor};
  padding: 8px 0 0 0;
  flex: 1;
`;

export const ItemContainer = styled.div<{ hasGrayBackground?: boolean, lastItem?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 0 16px;
  background-color: ${({
    hasGrayBackground,
    theme,
  }) => (hasGrayBackground ? theme.color.plcCard.grayBackgroundColor : 'transparent')};
  border-radius: ${({ lastItem }) => (lastItem ? '0 0 8px 8px' : '0')};
  padding-bottom: ${({ lastItem }) => (lastItem ? '12px' : '0')};
`;

export const ItemValue = styled.span`
  background-color: ${({ theme }) => theme.color.plcCard.valueBackgroundColor};
  border: 1px solid ${({ theme }) => theme.color.plcCard.borderColor};
  padding: 2px 8px;
  height: 22px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
`;

export const Header = styled.div`
  background-color: ${({ theme }) => theme.color.plcCard.inputBackgroundColor};
  padding: 8px 16px;
  border-radius: 4px 4px 0 0;
`;

export const Content = styled.div < { hasGrayBackground?: boolean, lastItem?: boolean }>`
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

export const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
`;

export const AnalogItemContainer = styled(ItemContainer)<{ hasGrayBackground?: boolean }>`
  display: flex;
`;

export const ValueContainer = styled.div`
  position: relative;
  top: 13px;
`;
